// Career Launch 진행 상태(진단·선정직무·정리한 직무정보·완료스텝) 클라이언트.
// 계정 기준으로 백엔드에 저장 → 다른 기기·브라우저에서도 그대로 이어간다.
// (기존 localStorage 대체)
const TOKEN_KEY = "platform_access_token";

function apiBase(): string {
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000";
}

function authHeaders(json = false): Record<string, string> {
  const headers: Record<string, string> = {};
  if (json) headers["Content-Type"] = "application/json";
  try {
    const t = window.localStorage.getItem(TOKEN_KEY);
    if (t) headers.Authorization = `Bearer ${t}`;
  } catch {
    // 익명 시도
  }
  return headers;
}

export type CareerDiagnosis = { percent: number; level: string; strengths: string[]; improvements: string[] };
export type CareerInterview = {
  practiced?: string[]; // 완료한 면접 유형(self|job|fit)
  results?: Record<string, string>; // 유형별 AI 총평(면접 종료 시 저장)
};
// Experience Bank — 4주 내내 재사용되는 중심 데이터. Week1에서 채굴하고 이후 이력서·자소서·면접이 참조.
export type ExperienceEntry = {
  id: string;
  experience: string; // 무엇 (예: "카페 아르바이트")
  period: string; // 기간
  role: string; // 역할
  actions: string[]; // 한 일
  results: string[]; // 성과
  skills: string[]; // 사용 기술
  competencies: string[]; // 역량
};
// 강점 스토리 — 경험을 면접·자소서용 짧은 이야기로(상황·행동·결과 + 강점). Week1에서 만들고 이후 재사용.
export type StrengthStory = { id: string; title: string; strength: string; situation: string; action: string; result: string };
// 목표 기업 후보 — 우리 공고에서 담거나 직접 추가. positionId 있으면 공고 상세로 연결.
export type TargetCompany = { name: string; role?: string; positionId?: string };
// 공고별 모의면접 한 문항의 채점 결과(카드형).
export type PostingInterviewItem = {
  question: string;
  answer: string;
  score: number;
  modelAnswer: string;
  feedback: string;
  strengths?: string[];
  improvements?: string[];
  /** 말로 답했을 때만 — 말하기 리포트(lib/launch/speaking). */
  speakingData?: import("./speaking").SpeakingData;
  /** 꼬리 질문(앞 답을 파고든 면접관 질문)이었나. */
  followUp?: boolean;
};
// 공고별 모의면접 기록 — 문항별 답변·점수·모범답안을 누적 저장해 나중에 다시 보고 오답노트처럼 재도전한다.
export type PostingInterviewLog = {
  id: string;
  company?: string;
  title?: string;
  at: string; // ISO
  source?: "posting" | "basic"; // 공고별 / 기본(내 서류) — 재도전 채점 방식 구분
  focus?: "self" | "job" | "fit" | "pressure" | "global"; // 기본 면접 유형(source="basic")
  posting?: import("./interview").InterviewJobPosting; // 공고별 재도전 채점에 필요
  items?: PostingInterviewItem[]; // 카드형(신규)
  // 구버전(채팅형) 호환 — 읽기 전용으로만 표시.
  messages?: { role: "bot" | "user"; text: string }[];
  report?: { strengths: string[]; improvements: string[]; modelAnswer: string } | null;
};
export type CareerProgress = {
  diagnosis?: CareerDiagnosis | null;
  // 진단 대화 기록(질문·답변) — '다시 보기' 시 대화를 그대로 복원하기 위해 저장.
  diagnosisTranscript?: Array<{ role: "bot" | "user"; text: string }> | null;
  experienceBank?: ExperienceEntry[];
  // 사전(1주차)·사후(4주차 수료) 진단 점수 — 향상도 산출용.
  diagnosisInitial?: { percent?: number } | null;
  diagnosisFinal?: { percent?: number } | null;
  selectedJobs?: string[];
  targetJob?: string | null; // 확정한 1순위 목표 직무명(2주차 서류 기준). week1/target 확정 시 기록.
  targetCompanies?: TargetCompany[]; // 1주차 '목표 기업 탐색'에서 담은 관심 기업 후보.
  postingInterviews?: PostingInterviewLog[]; // 4주차 공고별 모의면접 기록(최근 것부터).
  basicInterviews?: PostingInterviewLog[]; // 3주차 기본(내 서류) 모의면접 기록(최근 것부터).
  introPractice?: import("./speaking").IntroAttempt[]; // 1분 자기소개 연습 기록(최근 것부터, 최대 30).
  strengthStories?: StrengthStory[]; // 강점 스토리(면접·자소서 씨앗).
  materials?: string[];
  doneSteps?: string[];
  interview?: CareerInterview | null;
  finalFeedback?: { v?: number; sig?: string; text?: string } | null;
  // 점수화 리뉴얼 — 홈 스냅샷·완주 캡스톤에서 참조(각 카드가 저장한 래퍼 구조).
  careerReport?: { data?: { total?: number } } | null;
  careerScoreBefore?: number | null;
  jobRecommendation?: { data?: { jobs?: { role: string; fit: number }[] } } | null;
  scores?: {
    resume?: { data?: { total?: number } };
    cover?: { data?: { total?: number } };
    interview?: { data?: { total?: number } };
  } | null;
  storyBank?: { data?: { stories?: unknown[] } } | null;
  answerBank?: { data?: { answers?: { question: string; answer: string }[] } } | null;
  jdMatch?: { data?: { matchPercent?: number } } | null;
  passportMedia?: { photo?: string | null; background?: string | null } | null;
  passportLinks?: { type: string; url: string }[] | null;
};

export type PassportLink = { type: string; url: string };
// 커리어 패스포트 프로필/배경 사진·외부 링크 저장. null 로 비우기.
export async function savePassportMedia(patch: { photo?: string | null; background?: string | null; links?: PassportLink[] }): Promise<{ photo: string | null; background: string | null; links: PassportLink[] } | null> {
  try {
    const d = await req("/career-launch/passport/media", { method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(patch) });
    return { photo: (d.media as { photo: string | null })?.photo ?? null, background: (d.media as { background: string | null })?.background ?? null, links: (d.links as PassportLink[]) ?? [] };
  } catch {
    return null;
  }
}

async function req(path: string, init: RequestInit): Promise<Record<string, unknown>> {
  const res = await fetch(`${apiBase()}${path}`, init);
  const d = (await res.json().catch(() => null)) as (Record<string, unknown> & { ok?: boolean; message?: string }) | null;
  if (!res.ok || d?.ok !== true) throw new Error((d?.message as string) ?? "요청을 처리하지 못했어요.");
  return d;
}

// 전체 진행 상태 조회.
export async function fetchProgress(): Promise<CareerProgress> {
  const d = await req("/career-launch/progress", { headers: authHeaders() });
  return (d.state as CareerProgress) ?? {};
}

// 내 기수 익명 진행률 요약 — 함께 달리는 동기부여용(개인 식별정보 없음).
export type CohortStats = { peerCount: number; avgWeeks: number; myWeeks: number; aheadOfPct: number };
export async function fetchCohortStats(): Promise<CohortStats | null> {
  try {
    const d = await req("/career-launch/cohort-stats", { headers: authHeaders() });
    return (d.stats ?? null) as CohortStats | null;
  } catch {
    return null;
  }
}

// 기수 주차 오픈 일정(본인 기수) — 주차 게이팅에 사용.
export type WeekScheduleEntry = { week: number; opensAt: string | null; forceOpen: boolean };
export async function fetchWeekSchedule(): Promise<{ weekSchedule: WeekScheduleEntry[]; serverNow: string }> {
  try {
    const d = await req("/career-launch/week-schedule", { headers: authHeaders() });
    return {
      weekSchedule: (d.weekSchedule as WeekScheduleEntry[]) ?? [],
      serverNow: (d.serverNow as string) ?? new Date().toISOString()
    };
  } catch {
    return { weekSchedule: [], serverNow: new Date().toISOString() };
  }
}

// 일부 키만 갱신(얕은 병합). 갱신된 전체 상태를 반환.
export async function patchProgress(partial: Partial<CareerProgress>): Promise<CareerProgress> {
  const d = await req("/career-launch/progress", {
    method: "PATCH",
    headers: authHeaders(true),
    body: JSON.stringify(partial)
  });
  return (d.state as CareerProgress) ?? {};
}

// 한국 기업문화 학습 완료 — 완료 표시(doneSteps)는 서버만 기록하므로(자가위조 차단) 전용 경로로 보낸다.
// 갱신된 완료 스텝 목록을 반환한다.
export async function completeCultureLesson(lessonId: string): Promise<string[]> {
  const d = await req(`/career-launch/culture/${encodeURIComponent(lessonId)}/complete`, { method: "POST", headers: authHeaders(true) });
  return Array.isArray(d.doneSteps) ? (d.doneSteps as string[]) : [];
}

// ── Talent Passport — 검증된 Talent 프로필(Readiness·Verified 등급·다음 액션) ──
export type PassportTier = "preparing" | "bronze" | "silver" | "gold";
export type TalentPassport = {
  readiness: number;
  tier: PassportTier;
  verified: boolean;
  verifiedAt: string | null;
  breakdown: { direction: number; resume: number; cover: number; interview: number; experience: number; competency: number | null };
  scores: { career: number | null; resume: number | null; cover: number | null; interview: number | null };
  target: { role: string | null; recommended: { role: string; fit: number }[] };
  documents: { resumeReady: boolean; coverReady: boolean };
  experienceCount: number;
  languages: { language?: string; level?: string }[];
  jdMatch: number | null;
  activity: { applications: number; interviewsInvited: number; mockInterviews: number };
  gate: { diagnosisDone: boolean; resumeReady: boolean; experience3plus: boolean };
  nextActions: { key: string; label: string; reason: string; href: string }[];
  companyFeedback: { comment: string; result: string; org: string | null; at: string }[];
  companyActivity?: { org: string | null; status: string; at: string }[];
  shareViews?: number;
};

export async function fetchTalentPassport(): Promise<TalentPassport | null> {
  try {
    const d = await req("/career-launch/passport", { headers: authHeaders() });
    return (d.passport as TalentPassport) ?? null;
  } catch {
    return null;
  }
}

// 공유 토큰 발급(멱등) — 기업 제출용 공개 링크.
export async function sharePassport(): Promise<string | null> {
  try {
    const d = await req("/career-launch/passport/share", { method: "POST", headers: authHeaders() });
    return (d.token as string) ?? null;
  } catch {
    return null;
  }
}

export type SharedPassport = {
  name: string | null;
  readiness: number;
  tier: PassportTier;
  verified: boolean;
  verifiedAt: string | null;
  breakdown: TalentPassport["breakdown"];
  scores: TalentPassport["scores"];
  target: TalentPassport["target"];
  experienceCount: number;
  languages: { language?: string; level?: string }[];
  // 공유 카드 콘텐츠 — 내가 어떤 사람인지.
  headline?: string | null;
  subline?: string | null;
  pitch?: string | null;
  targetJobs?: string[];
  skills?: string[];
  highlights?: { head: string; period: string; bullets: string[] }[];
  photo?: string | null;
  background?: string | null;
  links?: { type: string; url: string }[];
  hasResume?: boolean;
  hasCover?: boolean;
};

// 공개(무인증) 원본 문서(이력서/자소서) 조회.
export async function fetchSharedDocument(token: string, type: "resume" | "cover"): Promise<{ name: string | null; content: Record<string, unknown> } | null> {
  try {
    const res = await fetch(`${apiBase()}/career-launch/passport/shared/${encodeURIComponent(token)}/document?type=${type}`);
    const d = (await res.json().catch(() => null)) as { ok?: boolean; name?: string | null; content?: Record<string, unknown> } | null;
    if (!res.ok || d?.ok !== true) return null;
    return { name: d.name ?? null, content: d.content ?? {} };
  } catch {
    return null;
  }
}

// 내 활동 타임라인(TalentEvent) — 학생 본인의 여정.
export type TimelineEvent = { type: string; at: string; metadata?: Record<string, unknown> | null };
export async function fetchMyTimeline(): Promise<TimelineEvent[]> {
  try {
    const d = await req("/career-launch/my-timeline", { headers: authHeaders() });
    return (d.events as TimelineEvent[]) ?? [];
  } catch {
    return [];
  }
}

// 공개(무인증) 공유 뷰 조회.
export async function fetchSharedPassport(token: string): Promise<SharedPassport | null> {
  try {
    const res = await fetch(`${apiBase()}/career-launch/passport/shared/${encodeURIComponent(token)}`);
    const d = (await res.json().catch(() => null)) as { ok?: boolean; passport?: SharedPassport } | null;
    if (!res.ok || d?.ok !== true) return null;
    return d.passport ?? null;
  } catch {
    return null;
  }
}
